import { useAppRouteParams } from "@/AppRoutes";
import {
  postAdminEditVersionCategory,
  postAdminRemoveVersionCategory,
} from "@/api/admin/versionCategory";
import {
  postEditVersionCategory,
  postRemoveVersionCategory,
} from "@/api/versionCategory";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import {
  DialogPhaseStatesType,
  useDialog,
} from "@/components/Dialog/helpers/hooks";
import { VersionCategoryRaw } from "@/domain/VersionCategories";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import { useCallback } from "react";

const useTranslation = translation.use(nsVersion);

/**
 * @description サイドナビとカテゴリー管理テーブルで共有するカテゴリー削除ダイアログ
 */
export const useVersionCategoryDeleteDialog = ({
  targetCategoryId,
  versionCategories,
  runReloadVersionCategories,
  isAdminMode,
  onDeleted,
  dialogPhase,
  setDialogPhase,
}: {
  targetCategoryId: string | null;
  versionCategories: VersionCategoryRaw[] | null;
  runReloadVersionCategories: () => Promise<void>;
  isAdminMode: boolean;
  onDeleted?: () => void;
} & DialogPhaseStatesType) => {
  const { t } = useTranslation();
  const { organizationId } = useAppRouteParams();
  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();
  const { renderDialogs } = useDialog({ dialogPhase, setDialogPhase });

  const runDelete = useCallback(async () => {
    if (!targetCategoryId || !versionCategories) {
      return;
    }

    const targetCategory = versionCategories.find(
      (category) => category.id === targetCategoryId
    );

    if (!targetCategory) {
      showErrorNotification(t.version.category.notFoundError());
      setDialogPhase(null);
      return;
    }

    const removeVersionCategory = isAdminMode
      ? postAdminRemoveVersionCategory
      : postRemoveVersionCategory;
    const editVersionCategory = isAdminMode
      ? postAdminEditVersionCategory
      : postEditVersionCategory;

    setDialogPhase("in_progress");

    try {
      // 配下バージョンの未分類への移行はサーバー側で行われる
      await removeVersionCategory({
        versionCategoryId: targetCategoryId,
        organizationId,
      });

      // 削除対象のカテゴリー以降のソート番号を更新
      // 削除成功後のベストエフォート処理なので、失敗しても削除自体は成功とする
      const categoriesToUpdate = versionCategories.filter(
        (category) => category.sort > targetCategory.sort
      );

      let renumberingError: unknown = null;
      for (const category of categoriesToUpdate) {
        try {
          await editVersionCategory({
            versionCategoryId: category.id,
            versionCategoryRawForm: {
              ...category,
              sort: category.sort - 1,
            },
            organizationId,
          });
        } catch (error) {
          renumberingError = error;
          break;
        }
      }

      // 削除は成功しているので必ずリロードする
      runReloadVersionCategories();

      if (renumberingError) {
        showErrorNotification(
          t.version.category.deleteSortError(),
          renumberingError
        );
      } else {
        showSuccessNotification(t.version.category.deleteSuccess());
      }
      setDialogPhase(null);
      onDeleted?.();
    } catch (error) {
      showErrorNotification(t.version.category.deleteError(), error);
      setDialogPhase(null);
      runReloadVersionCategories();
    }
  }, [
    t,
    targetCategoryId,
    versionCategories,
    organizationId,
    isAdminMode,
    onDeleted,
    setDialogPhase,
    showSuccessNotification,
    showErrorNotification,
    runReloadVersionCategories,
  ]);

  const renderVersionCategoryDeleteDialog = useCallback(() => {
    if (!dialogPhase || !targetCategoryId) return null;

    const targetCategory = versionCategories?.find(
      (category) => category.id === targetCategoryId
    );

    return renderDialogs({
      confirm: {
        title: t.version.category.deleteDialog.title(),
        displayMessage: targetCategory
          ? t.version.category.deleteDialog.message({
              categoryName: targetCategory.name,
            })
          : t.version.category.deleteDialog.messageWithoutName(),
        buttonText: t.version.category.deleteDialog.button(),
        inProgressButtonText:
          t.version.category.deleteDialog.inProgressButton(),
        onClick: runDelete,
        isThemeRed: true,
      },
    });
  }, [
    t,
    dialogPhase,
    targetCategoryId,
    versionCategories,
    renderDialogs,
    runDelete,
  ]);

  return { renderVersionCategoryDeleteDialog };
};
